import { useEffect, useState } from "react";
import { Loader2, Plus, Trash2, Backpack, Check, AlertTriangle, ShoppingBag } from "lucide-react";
import { api } from "@/lib/api";

const overline = "hr-overline text-blaze text-[11px]";
const inputCls = "w-full bg-white/5 border border-white/15 rounded px-3.5 py-2.5 outline-none focus:border-blaze text-bone text-sm placeholder:text-bone/30";
const STATUS = [
  ["packed", "Packed", "bg-forest/30 text-forest", Check],
  ["need", "Need to pack", "bg-saddle/20 text-saddle", AlertTriangle],
  ["acquire", "Need to acquire", "bg-red-500/15 text-red-300", ShoppingBag],
];
const SMAP = Object.fromEntries(STATUS.map((s) => [s[0], s]));

export function HuntPacking({ huntId }) {
  const [data, setData] = useState(null);
  const [name, setName] = useState("");

  const load = () => api.huntPacking(huntId).then(setData).catch(() => setData({ items: [], locker_available: [] }));
  useEffect(() => { load(); }, [huntId]);

  const add = async (itemName, fromLocker, category) => {
    if (!itemName?.trim()) return;
    await api.addPacking(huntId, { name: itemName.trim(), from_locker: !!fromLocker, category: category || "", status: fromLocker ? "packed" : "need" });
    setName(""); await load();
  };
  const cycle = async (item) => {
    const order = ["need", "packed", "acquire"];
    const next = order[(order.indexOf(item.status) + 1) % order.length];
    setData((d) => ({ ...d, items: d.items.map((x) => x.id === item.id ? { ...x, status: next } : x) }));
    await api.updatePacking(huntId, item.id, { status: next });
  };
  const del = async (id) => { setData((d) => ({ ...d, items: d.items.filter((x) => x.id !== id) })); await api.deletePacking(huntId, id); };

  if (!data) return <div className="py-8 grid place-items-center"><Loader2 className="animate-spin text-blaze" /></div>;

  return (
    <div data-testid="workspace-packing">
      <div className="flex items-center gap-2"><Backpack size={15} className="text-blaze" /><span className={overline}>Gear packing list</span></div>
      <div className="mt-4 space-y-2">
        {data.items.length === 0 && <p className="text-bone/40 text-sm">Nothing on your packing list yet. Add items or pull from your Gear Locker below.</p>}
        {data.items.map((item) => {
          const [, label, cls, Icon] = SMAP[item.status] || SMAP.need;
          return (
            <div key={item.id} className="flex items-center gap-3 bg-white/5 border border-white/10 rounded px-3.5 py-2.5" data-testid={`packing-${item.id}`}>
              <span className="flex-1 text-sm text-bone/85">{item.name}{item.category ? <span className="text-bone/30 text-xs ml-2">{item.category}</span> : null}</span>
              <button onClick={() => cycle(item)} className={`inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full ${cls}`} data-testid={`packing-status-${item.id}`}><Icon size={11} /> {label}</button>
              <button onClick={() => del(item.id)} className="text-bone/30 hover:text-red-400" data-testid={`packing-delete-${item.id}`}><Trash2 size={14} /></button>
            </div>
          );
        })}
      </div>
      <div className="flex gap-2 mt-3">
        <input value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add(name)} placeholder="Add gear item" className={inputCls} data-testid="packing-input" />
        <button onClick={() => add(name)} className="bg-white/10 hover:bg-blaze hover:text-white rounded px-4 transition-colors" data-testid="packing-add"><Plus size={17} /></button>
      </div>
      {data.locker_available.length > 0 && (
        <div className="mt-5 pt-4 border-t border-white/10">
          <span className="text-bone/40 text-xs uppercase tracking-wider">Add from your Gear Locker</span>
          <div className="mt-2.5 flex flex-wrap gap-2">
            {data.locker_available.slice(0, 30).map((g) => (
              <button key={g.name} onClick={() => add(g.name, true, g.category)} className="inline-flex items-center gap-1.5 border border-dashed border-white/20 hover:border-forest text-bone/70 hover:text-bone px-3 py-1.5 rounded-full text-sm transition-colors" data-testid={`locker-add-${(g.name || "").toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <Plus size={12} /> {g.name}
              </button>
            ))}
          </div>
        </div>
      )}
      <p className="text-bone/40 text-xs mt-4">Tap a status to cycle Packed → Need → Acquire. Partner-product suggestions are never shown as required gear.</p>
    </div>
  );
}
